import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, TrendingUp, Users, FileText, Star, BookOpen, BarChart3, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import MainNavigation from '../components/layout/MainNavigation';
import Footer from '../components/layout/Footer';

const ForTrainers = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      title: "Get Discovered by Top Organizations",
      description: "Your profile reaches decision-makers at leading companies actively seeking training expertise.",
      icon: Users,
      color: "from-blue-500 to-cyan-500"
    },
    {
      title: "Professional Proposal Management",
      description: "Create, track, and manage proposals with templates designed by industry experts.",
      icon: FileText,
      color: "from-purple-500 to-pink-500"
    },
    {
      title: "Track Your Business Growth",
      description: "Real-time analytics on revenue, bookings, and client satisfaction scores.",
      icon: BarChart3,
      color: "from-orange-500 to-red-500"
    },
    {
      title: "Build Your Digital Presence",
      description: "DigiTPro - Your professional training profile that showcases certifications and success stories.",
      icon: Star,
      color: "from-green-500 to-emerald-500"
    },
    {
      title: "Content Library Management",
      description: "Organize all your training materials, resources, and content in one secure place.",
      icon: BookOpen,
      color: "from-indigo-500 to-purple-500"
    },
    {
      title: "Smart Calendar Integration",
      description: "Never double-book again. Sync with your calendar and manage sessions effortlessly.",
      icon: Calendar,
      color: "from-rose-500 to-pink-500"
    }
  ];

  const stats = [
    { value: "18,000+", label: "Active Trainers", sublabel: "Growing community" },
    { value: "~100", label: "Organizations", sublabel: "Seeking trainers" },
    { value: "60,000+", label: "Training Mandays", sublabel: "Delivered annually" },
    { value: "4.8/5", label: "Avg. Rating", sublabel: "Client satisfaction" }
  ];

  const testimonials = [
    {
      name: "Dr. Abhishek Tiwari",
      role: "Independent Training Professional",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop",
      quote: "I've been an associate for 2 years. The platform gives me complete freedom to design content based on client needs. Backend support is exceptional.",
      rating: 5
    },
    {
      name: "Garima Goyal",
      role: "Corporate Training Specialist",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop",
      quote: "The proposal management system has transformed how I present my services. I've doubled my client base in just 6 months.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <MainNavigation />

      {/* Hero Section - Asymmetric Modern Design */}
      <section className="relative overflow-hidden py-20 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 opacity-60"></div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 left-20 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center bg-blue-100 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse mr-2"></span>
                <span className="text-sm font-semibold text-blue-800">For Training Professionals</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
                Turn Your Training
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
                  Expertise Into Growth
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Join 18,000+ training professionals who've transformed their careers. Get direct access to organizations, manage everything in one place, and focus on what you do best—training.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/login')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-xl hover:shadow-2xl transition-all px-8"
                >
                  Start Your Free Profile
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-gray-300 hover:border-blue-600"
                >
                  Watch Demo
                </Button>
              </div>

              <div className="mt-8 flex items-center space-x-6">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 border-2 border-white"></div>
                  ))}
                </div>
                <div className="text-sm text-gray-600">
                  <div className="font-semibold text-gray-900">Join 18,000+ trainers</div>
                  <div className="flex items-center">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 mr-1" />
                    <span>4.8/5 average rating</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative lg:pl-12">
              {/* Stats Cards with organic layout */}
              <div className="grid grid-cols-2 gap-6">
                {stats.map((stat, index) => (
                  <Card 
                    key={index}
                    className={`border-none shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 ${
                      index === 1 || index === 2 ? 'mt-8' : ''
                    }`}
                  >
                    <CardContent className="p-6">
                      <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">
                        {stat.value}
                      </div>
                      <div className="text-gray-900 font-semibold text-sm">{stat.label}</div>
                      <div className="text-gray-500 text-xs">{stat.sublabel}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section - Modern Card Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional tools designed specifically for training experts who want to grow their business
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card 
                  key={index}
                  className="group border-none shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                >
                  <CardContent className="p-8">
                    <div className={`w-14 h-14 bg-gradient-to-br ${benefit.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </CardContent>
                  <div className={`h-1 bg-gradient-to-r ${benefit.color}`}></div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials - Modern Layout */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Loved by Training Professionals
            </h2>
            <p className="text-xl text-gray-600">Real stories from trainers who've grown with us</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-none shadow-xl">
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.role}</div>
                      <div className="flex mt-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed italic">"{testimonial.quote}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Grow Your Training Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of trainers who've already made the switch. It's free to start.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/login')}
            className="bg-white text-blue-600 hover:bg-gray-100 shadow-xl px-12"
          >
            Create Your Free Profile
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForTrainers;
